import { motion } from 'framer-motion'
import { ExternalLink, Code2, BookOpen } from 'lucide-react'

const Tutorial = () => {
  const tutorials = [
    {
      title: 'Basic C Language Fundamentals',
      description: 'Master the foundations of C programming with comprehensive tutorials and 90+ practice codes. Learn variables, loops, functions, pointers, arrays, and more with hands-on examples codes.',
      image: '/harun.png',
      mentor: 'Harun Or Rashid',
      level: 'Beginner to Intermediate',
      topics: ['Variables', 'Control Flow', 'Functions', 'Arrays', 'Pointers', 'Strings', 'Structures'],
      duration: 'Self-paced',
      link: 'https://c-tutorial-nine.vercel.app/',
      highlight: true
    }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  }

  return (
    <section id="tutorials" className="py-20 px-6 bg-slate-900/50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-violet-400 to-cyan-500 bg-clip-text text-transparent">
              Learning Tutorials
            </span>
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Comprehensive courses with hands-on practice to master programming fundamentals.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid md:grid-cols-1 gap-8"
        >
          {tutorials.map((tutorial, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              className={`group relative overflow-hidden rounded-xl border transition-all duration-300 ${
                tutorial.highlight
                  ? 'border-violet-500/50 bg-gradient-to-br from-slate-800 to-slate-900 shadow-lg shadow-violet-500/20'
                  : 'border-white/10 bg-slate-800/50'
              }`}
            >
              {/* Background gradient on hover */}
              <div className="absolute inset-0 bg-gradient-to-r from-violet-500/0 via-cyan-500/0 to-violet-500/0 group-hover:from-violet-500/10 group-hover:via-cyan-500/10 group-hover:to-violet-500/10 transition-all duration-500" />

              <div className="relative p-8">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  {/* Image Section */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                    className="flex justify-center"
                  >
                    <div className="relative w-48 h-48 rounded-xl overflow-hidden border-2 border-violet-500/30 group-hover:border-violet-500/60 transition-all">
                      <img
                        src={tutorial.image}
                        alt={tutorial.mentor}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent flex items-end justify-center pb-4">
                        <p className="text-white font-semibold text-center">{tutorial.mentor}</p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Content Section */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                    className="space-y-4"
                  >
                    <div className="flex items-center gap-2">
                      <Code2 className="w-6 h-6 text-violet-400" />
                      <h3 className="text-2xl font-bold text-white">{tutorial.title}</h3>
                    </div>

                    <p className="text-white/70 leading-relaxed">{tutorial.description}</p>

                    <div className="flex flex-wrap gap-2 py-2">
                      {tutorial.topics.map((topic, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 rounded-full bg-violet-500/20 text-violet-300 text-sm border border-violet-500/30"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center gap-4 pt-4 text-white/60 text-sm">
                      <span className="flex items-center gap-1">
                        <BookOpen className="w-4 h-4" />
                        {tutorial.level}
                      </span>
                      <span>•</span>
                      <span>{tutorial.duration}</span>
                      <span>•</span>
                      <span className="text-violet-400 font-semibold">90+ Practice Codes</span>
                    </div>

                    <motion.a
                      href={tutorial.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.05, x: 5 }}
                      whileTap={{ scale: 0.95 }}
                      className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-lg bg-gradient-to-r from-violet-600 to-cyan-600 text-white font-semibold hover:shadow-lg hover:shadow-violet-500/50 transition-all duration-300"
                    >
                      Start Learning
                      <ExternalLink className="w-4 h-4" />
                    </motion.a>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

export default Tutorial
